import React from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarImage } from '@codes/ui-kit/components/ui/avatar';
import { Button } from '@codes/ui-kit/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { EllipsisVertical, Heart, MessageCircle } from 'lucide-react';

import EmptyUserIcon from '/assets/icons/user.svg';

export interface CommentsSectionProps {
    onReport?: () => void;
    onDelete?: () => void;
    onSubmitComment?: (comment: string) => void;
}

export const CommentsSection: React.FC<CommentsSectionProps> = ({ onReport, onDelete, onSubmitComment }) => {
    const { t } = useTranslation();

    return (
        <div className="h-full bg-white dark:bg-[#1C1C1D] shadow-[0_0_10px_rgba(0,0,0,0.06)] border border-chatbot-line rounded-[16px] overflow-hidden flex flex-col py-6 px-[26px] max-md:max-h-[50vh] max-md:overflow-auto">
            <div className="flex-1 flex flex-col overflow-hidden">
                <div className="text-[20px] font-medium mb-[23px] whitespace-pre-line">
                    {t('agent.chat.reviewPrompt')}
                </div>
                <div className="flex items-center gap-1 text-sub text-sm font-medium bg-[#F4F5F5] dark:bg-[#3A3C40] py-[5px] px-[9px] rounded-md">
                    <MessageCircle className="w-4 h-4 text-point" />2{t('agent.chat.commentCount')}
                </div>
                <ul className="flex-1 h-full overflow-auto">
                    <li className="border-b border-[#F0F0F0] dark:border-[#5F5F5F] py-[18px]">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <Avatar className="w-6 h-6 bg-[#F4F5F5] border border-chatbot-line p-0">
                                    <AvatarImage src={EmptyUserIcon} />
                                </Avatar>
                                <div className="text-xs font-medium ml-[6px]">{t('agent.chat.anonymousUser')}</div>
                                <div className="text-[10px] text-[#9FA2A7]">・</div>
                                <div className="text-[10px] text-[#9FA2A7]">10{t('agent.chat.minute')}</div>
                            </div>
                            <DropdownMenu>
                                <DropdownMenuTrigger className="group" onClick={e => e.stopPropagation()}>
                                    <EllipsisVertical className="w-4 h-4 text-sub group-hover:text-icon-hover" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="end"
                                    className="min-w-[108px] px-3 py-[6px] rounded-lg outline-none"
                                >
                                    <DropdownMenuItem
                                        className="text-xs justify-center"
                                        onClick={e => {
                                            e.stopPropagation();
                                            onReport?.();
                                        }}
                                    >
                                        {t('agent.chat.report')}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className="my-[6px] bg-[#F4F5F5]"></DropdownMenuSeparator>
                                    <DropdownMenuItem
                                        className="text-xs justify-center text-[#F34822] focus:text-[#F34822]"
                                        onClick={e => {
                                            e.stopPropagation();
                                            onDelete?.();
                                        }}
                                    >
                                        {t('agent.chat.delete')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                        <div className="mt-1 text-[13px]">
                            Lorem ipsum dolor sit amet consectetur. Amet tortor donec dolor diam eget amet pretium
                            cursus aliquam.
                        </div>
                        <div className="mt-2">
                            <Heart className="w-4 h-4 text-sub cursor-pointer" strokeWidth={1.5} />
                        </div>
                    </li>
                </ul>
            </div>

            <div className="mt-auto sticky bottom-0 bg-white dark:bg-[#1C1C1D] pt-[14px]">
                <div className="h-[42px] flex items-center justify-between gap-1 px-[10px] border border-chatbot-line rounded-lg overflow-auto">
                    <input
                        className="flex-1 text-sm outline-0 bg-transparent"
                        type="text"
                        placeholder={t('agent.chat.placeholder')}
                    />
                    <Button size="sm" className="h-[26px]">
                        {t('agent.chat.submit')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
